import React, { useEffect, useState } from "react";
import styles from "./BlogDetail.module.css";
import { useParams } from "react-router-dom";
import axiosPrivate from "../../../hooks/axiosPrivate";
import { useFormik } from "formik";
import * as Yup from "yup";
import LoaderContext from "../../../context/Loader/LoaderContext";
import { useContext } from "react";
import { showErrorToast, showSuccessToast } from "../../../Toster";
import { FaUser, FaComment } from "react-icons/fa";
import SEO from "../../common/SEO";
import "react-toastify/dist/ReactToastify.css";
import { classNames } from "primereact/utils";
import CustomButton from "../../custom-button";

const contactFormSchema = Yup.object().shape({
  name: Yup.string().required("Name is Required"),
  mobile: Yup.string()
    .required("Mobile number is required")
    .matches(/^[6-9]\d{9}$/, {
      message: "Please enter valid number.",
      excludeEmptyString: false,
    })
    .min(10, "Mobile number must be of 10 digits")
    .max(10, "Mobile number must be of 10 digits"),
  email: Yup.string()
    .matches(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/, "Invalid email address")
    .required("Email is Required"),
  message: Yup.string().required("Message is Required"),
});

function BlogDetail() {
  const { id } = useParams();
  const [Blog, setBlog] = useState([]);
  const { setLoaderCheck } = useContext(LoaderContext);
  const [comment, setComment] = useState([]);

  const contactForm = {
    name: "",
    mobile: "",
    email: "",
    message: "",
    status: "Pending",
  };

  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    resetForm,
  } = useFormik({
    initialValues: contactForm,
    validationSchema: contactFormSchema,
    onSubmit: (values) => {
      setLoaderCheck(true);
      axiosPrivate
        .post("comment", values)
        .then((res) => {
          setLoaderCheck(false);
          showSuccessToast("Successfully Posted");
          resetForm();
        })
        .catch((errors) => {
          setLoaderCheck(false);
          showErrorToast("Internal Server Error");
        });
    },
  });

  // const formattedDate = created_at.toISOString().substring(0, 10);

  useEffect(() => {
    const options = {
      headers: { "content-type": "application/json" },
    };
    setLoaderCheck(true);
    axiosPrivate
      .get(`comment?status=Published`, options)
      .then((comment) => {
        setLoaderCheck(false);
        setComment(comment.data.data);
      })
      .catch((error) => {
        setLoaderCheck(false);
        if (error.response) {
          showErrorToast("Internal Server Error");
        }
      });
  }, []);

  useEffect(() => {
    const options = {
      headers: { "content-type": "application/json" },
    };
    setLoaderCheck(true);
    axiosPrivate
      .get(`/blogs/slug/${id}`, options)
      .then((blog) => {
        setLoaderCheck(false);
        setBlog(blog?.data?.data);
      })
      .catch((error) => {
        setLoaderCheck(false);
        if (error.response) {
          showErrorToast("Internal Server Error");
        }
      });
  }, []);

  const formatDate = (dateString) => {
    const dateObject = new Date(dateString);
    const day = String(dateObject.getDate()).padStart(2, "0");
    const month = String(dateObject.getMonth() + 1).padStart(2, "0");
    const year = dateObject.getFullYear();

    return `${day}/${month}/${year}`;
  };

  // const arrayToString = (array) => {
  //   let parcedArray=[]
  //   if(array!=undefined) {
  //   parcedArray=JSON.parse(array)
  //   }
  //   return parcedArray.map((item) => item.text).join(', ');
  // }
  const arrayToString = (array) => {
    if (!array) return "";
    let parsedArray;
    try {
      parsedArray = JSON.parse(array);
      if (Array.isArray(parsedArray)) {
        return parsedArray.map((item) => item.text).join(", ");
      }
    } catch (e) {
      // If it's not JSON, assume it's a simple string
      return array;
    }

    return "";
  };

  return (
    <>
      {/* detail section */}

      <SEO
        title={Blog?.meta_title}
        description={Blog?.meta_description}
        keywords={Blog?.keywords}
        canonical={Blog?.canonical_tags}
        tags={Blog?.tags}
        ogTitle={Blog?.og_title}
        ogDescription={Blog?.og_description}
        ogImage={Blog?.og_image}
        ogUrl={Blog?.og_image}
        twitterTitle={Blog?.meta_title}
        twitterDescription={Blog?.meta_description}
        twitterImage={Blog?.og_image}
        structuredData={Blog?.structured_data}
        headSchema={Blog?.head_schema}
        bodySchema={Blog?.body_schema}
      />

      <div
        className={classNames(
          styles.blogDetailPage,
          "container section-spacing-top"
        )}
      >
        <div className="row">
          <div
            className={`${styles.imageAndTagsContainer} col-lg-6 col-md-12 col-sm-12`}
          >
            {/* Left column for Image and Tags */}
            <div className={styles.leftColumn}>
              <div className={styles.blogImage}>
                <img
                  className="img-fluid"
                  src={
                    process.env.REACT_APP_COMMON_FILE_URL +
                    "blogs/" +
                    Blog?.banner_image
                  }
                  alt={Blog?.banner_image_alt}
                  loading="lazy"
                />
                <div className={styles.descriptionContainer}>
                  {/* <p className={styles.blogDescription}>{Blog?.description}</p> */}
                  <p
                    className={styles.blogDescription}
                    dangerouslySetInnerHTML={{ __html: Blog?.description }}
                  ></p>
                </div>
              </div>
              <div className={styles.tagContainer}>
                <h3 className={styles.tag}>Tags:</h3>
                <div className={styles.tagFields}>
                  <div className={styles.productTags}>
                    <span className={styles.tagText}>
                      {
                        (() => {
                          let tags = Blog?.tags;

                          // Try parsing if it's a stringified array
                          if (typeof tags === 'string' && tags.trim().startsWith('[')) {
                            try { tags = JSON.parse(tags); } catch { }
                          }

                          // Normalize to array of strings
                          const list = Array.isArray(tags)
                            ? tags.map(tag => typeof tag === 'string' ? tag : tag?.text).filter(Boolean)
                            : [tags].filter(Boolean);

                          return list.join(', ');
                        })()
                      }
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Right column for Leave a Reply */}
          <div className="col-lg-6 col-md-12 col-sm-12">
            <div className={styles.rightColumn}>
              <div className={styles.replyContainer}>
                {/* <h3 className={`${styles.tag} ${styles.leaveReply}`}>
                    Leave a Reply
                  </h3>
                  <p className="text-small">
                    Your email address will not be published. Required fields are
                    marked *
                  </p> */}

                <form onSubmit={handleSubmit} className="formWrapper">
                  <div className="formWrapperHeader">
                    <h2 className="formWrapperTitle">Leave a Reply</h2>
                    <p className="formWrapperDescription">
                      Your email address will not be published. Required fields
                      are marked
                    </p>
                  </div>
                  <div className="formWrapperContent">
                    <div className="formWrapperRow">
                      <div className="formWrapperCol">
                        <label htmlFor="name" className="formWrapperLabel">
                          Name<span className="formWrapperError">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          id="name"
                          placeholder="Name"
                          className="formWrapperInput"
                          value={values.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        {touched.name && errors.name ? (
                          <div className="formWrapperError">{errors.name}</div>
                        ) : null}
                      </div>
                      <div className="formWrapperCol">
                        <label htmlFor="mobile" className="formWrapperLabel">
                          Mobile<span className="formWrapperError">*</span>
                        </label>
                        <input
                          type="text"
                          name="mobile"
                          id="mobile"
                          placeholder="Mobile Number"
                          className="formWrapperInput"
                          value={values.mobile}
                          onChange={(event) => {
                            const numRejex = /^[0-9\b]+$/;
                            if (
                              event.target.value === "" ||
                              (numRejex.test(event.target.value) &&
                                event.target.value.length <= 10)
                            ) {
                              handleChange(event);
                            }
                          }}
                          onBlur={handleBlur}
                        />
                        {touched.mobile && errors.mobile ? (
                          <div className="formWrapperError">
                            {errors.mobile}
                          </div>
                        ) : null}
                      </div>
                    </div>
                    <div className="formWrapperRow">
                      <div className="formWrapperCol">
                        <label htmlFor="email" className="formWrapperLabel">
                          Email<span className="formWrapperError">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          id="email"
                          placeholder="Email"
                          className="formWrapperInput"
                          value={values.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        {touched.email && errors.email ? (
                          <div className="formWrapperError">{errors.email}</div>
                        ) : null}
                      </div>
                    </div>
                    <div className="formWrapperRow">
                      <div className="formWrapperCol">
                        <label htmlFor="message" className="formWrapperLabel">
                          Message<span className="formWrapperError">*</span>
                        </label>
                        <textarea
                          name="message"
                          id="message"
                          cols="20"
                          rows="10"
                          className="formWrapperTextarea"
                          placeholder="Message"
                          value={values.message}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        {touched.message && errors.message ? (
                          <div className="formWrapperError">
                            {errors.message}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <div className="formWrapperFooter">
                    <CustomButton
                      text="Post Comment"
                      type="submit"
                      className="mb-0"
                    />
                  </div>
                  {/* <div
                      className={`row row-cols-2 g-0 justify-content-between ${styles.formContainer}`}
                    >
                      <div className={`${styles.fieldWidth} col-12`}>
                        <div className={`col-sm-6 col-12 ${styles.contactField}`}>
                        <label htmlFor="name">Name<span className="validationIcon">*</span></label>
                          <input
                            type="text"
                            name="name"
                            id="name"
                            placeholder="Name"
                            className="mb-0 form-input"
                            value={values.name}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          {touched.name && errors.name ? (
                            <div className="invalidDataError">
                              {errors.name}
                            </div>
                          ) : null}
                        </div>
                        <div className={`col-sm-6 col-12 ${styles.contactField}`}>
                        <label htmlFor="mobile" > Mobile<span className="validationIcon">*</span></label>
                          <input
                            type="text"
                            name="mobile"
                            id="mobile"
                            placeholder="Mobile Number"
                            className="mb-0 form-input"
                            value={values.mobile}
                            onChange={(event) => {
                              const numRejex = /^[0-9\b]+$/;
                              if (
                                event.target.value === "" ||
                                (numRejex.test(event.target.value) &&
                                  event.target.value.length <= 10)
                              ) {
                                handleChange(event);
                              }
                            }}
                            onBlur={handleBlur}
                          />
                          {touched.mobile && errors.mobile ? (
                            <div className="invalidDataError">
                              {errors.mobile}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      <div className={`col-12 ${styles.contactField}`}>
                      <label htmlFor="email">Email<span className="validationIcon">*</span></label>
                        <input
                          type="email"
                          name="email"
                          id="email"
                          placeholder="Email"
                          className="mb-0 form-input"
                          value={values.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        {touched.email && errors.email ? (
                          <div className="invalidDataError">
                            {errors.email}
                          </div>
                        ) : null}
                      </div>
                    </div>
                    <div className={`col-12 ${styles.contactField}`}>
                        <label htmlFor="message">Message<span className="validationIcon">*</span></label>
                          <textarea
                            name="message"
                            id="message"
                            cols="20"
                            rows="10"
                            className="mb-0 form-input"
                            placeholder="Message"
                            value={values.message}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          {touched.message && errors.message ? (
                            <div className="invalidDataError">
                              {errors.message}
                            </div>
                          ) : null}
                    </div>
                    <div
                      className={`${styles.submitBtnContainer} col-12 ${styles.commnent}`}
                    >
                      <button type="submit" className={styles.careerSubmitBtn}>
                        Post Comment
                      </button>
                    </div> */}
                </form>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.publishedComments}>
          {comment.filter((c) => c.status === "Published").length > 0 && (
            <div>
              <h3>Comments:</h3>
              {comment
                .filter((c) => c.status === "Published")
                .map((c) => (
                  <div key={c.id} className={styles.commentContainer}>
                    <div className={styles.commentContent}>
                      <div className={styles.container}>
                        <div className={styles.nameSection}>
                          <FaUser />
                        </div>
                        <div className={styles.name}> {c.name} </div>
                        <div className={styles.dateSection}>
                          {formatDate(c.created_at)}
                        </div>
                      </div>
                      <div className={styles.messageSection}>
                        <div className={styles.commentMessage}>
                          <FaComment />
                        </div>{" "}
                        <div className={styles.message}> {c.message} </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
export default React.memo(BlogDetail);
